import { Injectable } from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {
  Flow,
  EventClass,
  EventCreate,
  EventType,
  EventTypeCreate,
  GetParams,
  GetResponse,
  Event,
  PostResponse, FlowCreate, FlowService, EventTriggerCreate, EventTrigger,
} from '@app/shared';
import {forkJoin, lastValueFrom, Observable} from 'rxjs';
import {convertGetParamsToHttpParams, deepClean, deepCompare, deepGet} from '@app/core';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {Form, FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FlowEventService {
  /** URLs principales relacionadas con Event **/
  flowUrl = environment.coreApiBaseUrl + '/api/flow';
  flowServiceUrl = environment.coreApiBaseUrl + '/api/FlowService';
  eventUrl = environment.coreApiBaseUrl + '/api/event';
  eventClassUrl = environment.coreApiBaseUrl + '/api/event-class';
  eventTypeUrl = environment.coreApiBaseUrl + '/api/event-type';
  eventTriggerUrl = environment.coreApiBaseUrl + '/api/event-trigger';
  eventTriggerTypeUrl = environment.coreApiBaseUrl +'/api/EventTriggerType';

  mojitoDynamicFieldOptions = [
    {enum: 'EtaNave', label:'Eta Nave'},
    {enum: 'Llegado', label:'Llegado'},
    {enum: 'Porteo', label:'Porteo'},
    {enum: 'AlmacenIntermedio', label:'Almacén Intermedio'},
    {enum: 'Planificado', label:'Planificado'},
    {enum: 'Retiro', label:'Retiro'},
    {enum: 'EnTransito', label:'En tránsito'},
    {enum: 'EnSite', label:'En Site'},
    {enum: 'Descargado', label:'Descargado'},
    {enum: 'Devolucion', label:'Devolución'},
    {enum: 'ServicioFinalizado', label:'Servicio Finalizado'},
  ]

  constructor(private http: HttpClient) {}

  /** Metodos de obtener/crear/actualizar/eliminar Event **/
  getEvent(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.eventUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createEvent(newEvent: EventCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.eventUrl, newEvent);
  }

  updateEvent(event: any): Observable<any> {
    return this.http.put(this.eventUrl, event);
  }

  patchEvent(payload: any): Observable<any> {
    return this.http.patch(this.eventUrl, payload);
  }

  deleteEvent(eventIds: number[]): Observable<any> {
    return this.http.delete(this.eventUrl, {body: {ids: eventIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar EventClass **/
  getEventClass(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.eventClassUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createEventClass(newEventClass: {isActive: boolean, name: string}): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.eventClassUrl, newEventClass);
  }

  updateEventClass(eventClass: EventClass): Observable<any> {
    return this.http.put(this.eventClassUrl, eventClass);
  }

  deleteEventClass(eventClassIds: number[]): Observable<any> {
    return this.http.delete(this.eventClassUrl, {body: {ids: eventClassIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar EventType **/
  getEventType(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.eventTypeUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createEventType(newEventType: EventTypeCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.eventTypeUrl, newEventType);
  }

  updateEventType(eventType: EventType): Observable<any> {
    return this.http.put(this.eventTypeUrl, eventType);
  }

  deleteEventType(eventTypeIds: number[]): Observable<any> {
    return this.http.delete(this.eventTypeUrl, {body: {ids: eventTypeIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar EventTrigger **/

  getEventTrigger(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.eventTriggerUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createEventTrigger(newEventTrigger: EventTriggerCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.eventTriggerUrl, newEventTrigger);
  }

  updateEventTrigger(eventTrigger: any): Observable<any> {
    return this.http.put(this.eventTriggerUrl, eventTrigger);
  }

  /** Metodos de obtener/crear/actualizar/eliminar Flow **/
  getFlow(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.flowUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createFlow(newFlow: any): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.flowUrl, newFlow);
  }

  updateFlow(flow: any): Observable<any> {
    return this.http.put(this.flowUrl, flow);
  }

  deleteFlow(flowIds: number[]): Observable<any> {
    return this.http.delete(this.flowUrl, {body: {ids: flowIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar FlowService **/
  getFlowService(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.flowServiceUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createFlowService(newFlowService: {flowId: number, serviceId: number}): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.flowServiceUrl, newFlowService);
  }

  updateFlowService(flowService: FlowService): Observable<any> {
    return this.http.put(this.flowServiceUrl, flowService);
  }

  deleteFlowService(flowServiceIds: number[]): Observable<any> {
    return this.http.delete(this.flowServiceUrl, {body: {ids: flowServiceIds}});
  }


  getEventTriggerType(getParams:GetParams):Observable<GetResponse>{
    return this.http.get<GetResponse>(this.eventTriggerTypeUrl, {params:convertGetParamsToHttpParams(getParams)})
  }

  drop(event: CdkDragDrop<any[]>, events: FormArray): void {
    const eventsArray = events as FormArray;
    const controls = eventsArray.controls;

    // 1. Verificar si las posiciones de inicio/fin tienen eventos especiales
    const isStartSpecial = controls[0].get('name')?.value === 'Tarea creada';
    const isEndSpecial = controls[controls.length - 1].get('name')?.value === 'Tarea finalizada';

    // 2. Determinar si el movimiento es inválido
    const isMovingProtectedStart = event.previousIndex === 0 && isStartSpecial;
    const isMovingProtectedEnd = event.previousIndex === controls.length - 1 && isEndSpecial;
    const isTargetingProtectedStart = event.currentIndex === 0 && isStartSpecial;
    const isTargetingProtectedEnd = event.currentIndex === controls.length - 1 && isEndSpecial;

    // 3. Bloquear solo si se afecta un evento especial en su posición
    if (isMovingProtectedStart || isMovingProtectedEnd || isTargetingProtectedStart || isTargetingProtectedEnd) {
      return; // Cancelar movimiento protegido
    }

    // 4. Realizar el movimiento
    const newControls = [...controls];
    moveItemInArray(newControls, event.previousIndex, event.currentIndex);

    // 5. Actualizar sequences
    newControls.forEach((ctrl, idx) => {
      ctrl.get('sequence')?.setValue(idx + 1, {emitEvent: false});
    });

    // 6. Actualizar el FormArray
    eventsArray.controls = newControls;
    eventsArray.updateValueAndValidity();
  }

  removeEvent(index: number, events: FormArray): void {
    // 1. Obtener el valor de 'sequence' del elemento que se eliminará
    const deletedSequence = (events.at(index) as FormGroup).get('sequence')?.value;

    // 2. Eliminar el elemento
    events.removeAt(index);

    // 3. Actualizar las secuencias de los elementos restantes
    for (let i = index; i < events.length; i++) {
      const currentGroup = events.at(i) as FormGroup;
      const currentSequence = currentGroup.get('sequence')?.value;

      // 4. Reducir solo si la secuencia es mayor que la eliminada
      if (currentSequence > deletedSequence) {
        currentGroup.get('sequence')?.setValue(currentSequence - 1);
      }
    }
  }

  setEvents(flow: Flow, events: FormArray, fb: FormBuilder) {
    // Limpia el FormArray y agrega controles basados en el flujo seleccionado
    events.clear();
    if (flow?.events) {
      flow?.events.sort((a: EventType, b: EventType) => a.sequence - b.sequence).forEach(evt => {
        events.push(fb.group({
          id: [evt.id, Validators.required],
          name: [evt.name, Validators.required],
          sequence: [evt.sequence, Validators.required],
          icon: [evt.icon, Validators.required],
          color: [evt.color, Validators.required],
          activator: [evt.activator, Validators.required],
        }));
      });
    }
  }

  isFormsValids(eventForm: FormGroup, activatorForm: FormGroup): boolean {
    const isEventValid = eventForm!.get('color')?.value && eventForm!.get('icon')?.value && eventForm!.get('name')?.value && eventForm!.get('sequence')?.value
    const isManual = activatorForm!.get('name')?.value === 'Manual';
    return isEventValid ? (isManual ? true : !!activatorForm?.valid) : false;
  }

  /** Metodo para editar las esntidades de Flow, EventType y EventTrigger **/
  async editFlow(originalData: Flow, events: FormArray, flowName?: string): Promise<Flow> {
    originalData.events = originalData.events || [];

    const editedData: Flow = {
      id: originalData.id,
      events: events.value || [],
      name: flowName || originalData.name,
    }

    try {
      if (originalData.name !== editedData.name) {
        const flowParams = {
          id: editedData.id,
          name: editedData.name,
          isActive: true,
          isNative: true,
        }

        await lastValueFrom(this.updateFlow(flowParams));
      }

      const changes = {
        added: this.detectAddedEvents(originalData, editedData),
        deleted: this.detectDeletedEvents(originalData, editedData),
        updated: this.detectUpdatedEvents(originalData, editedData),
      };

      if (changes.added.length > 0) {
        for (const et of changes.added) {
          if (et.id > 0) et.id = et.id * -1;
          const updatedFlow = await this.createOrEditEvents(et, false, editedData, originalData.id);
          editedData.events = updatedFlow.events;
        }
      }

      if (changes.deleted.length > 0) {
        const idsToDelete = changes.deleted.map(i => (i.id));
        await lastValueFrom(this.deleteEventType(idsToDelete));
      }

      if (changes.updated.length > 0) {
        let requests: Observable<any>[] = [];
        for (const et of changes.updated) {
          const {eventTriggerTypeId, eventTypePayload} = this.setEventTypePayload(et, true, editedData.id, originalData.id);
          const eventTriggerPayload = await this.setEventTriggerPayload(et, eventTriggerTypeId, true, et.id)

          requests = [
            ...requests,
            this.updateEventType(eventTypePayload),
            this.updateEventTrigger(eventTriggerPayload)
          ];
        }

        await lastValueFrom(forkJoin(requests));
      }

      // Devolver el flujo actualizado con los IDs correctos
      return editedData;

    } catch (e) {
      console.error(e);
      throw e; // Re-lanzar el error para que el componente pueda manejarlo
    }
  }

  private detectAddedEvents(initial: Flow, edited: Flow): EventType[] {
    return edited.events!.filter(event =>
      event.id < 0 || !initial.events!.some(e => e.id === event.id)
    );
  }

  private detectDeletedEvents(initial: Flow, edited: Flow): EventType[] {
    return initial.events!.filter(initialEvent =>
      !edited.events!.some(e => e.id === initialEvent.id)
    );
  }

  private detectUpdatedEvents(initial: Flow, edited: Flow): EventType[] {
    return edited.events!.filter(editableEvent => {
      if (editableEvent.id < 0) return false; // Ignorar nuevos

      const originalEvent = initial.events!.find(e => e.id === editableEvent.id);
      return originalEvent && this.hasEventChanged(originalEvent, editableEvent);
    });
  }

  private hasEventChanged(original: EventType, edited: EventType): boolean {
    // Propiedades a comparar con sus rutas
    const propertiesToCompare = [
      'sequence',
      'name',
      'icon',
      'color',
      'activator.name',
      'activator.location',
      'activator.value'
    ];

    for (const path of propertiesToCompare) {
      const originalValue = deepGet(original, path);
      const editedValue = deepGet(edited, path);

      if (!deepCompare(originalValue, editedValue)) {
        return true; // Hay cambios
      }
    }

    return false; // No hay cambios
  }

  async createOrEditEvents(et: EventType, isEdit: boolean, flow: Flow, originalFlowId: number): Promise<Flow> {
    const {eventTriggerTypeId, eventTypePayload} = this.setEventTypePayload(et, isEdit, flow.id, originalFlowId);

    const eventTypeRequest = isEdit
      ? this.updateEventType(deepClean(eventTypePayload))
      : this.createEventType(deepClean(eventTypePayload))

    // Se crea en BD el EventType con los datos dados
    const eventTypeRsp = await lastValueFrom(eventTypeRequest);

    if ((isEdit && !!et.eventTriggers?.length) || !isEdit) {

      const eventTriggerPayload = await this.setEventTriggerPayload(et, eventTriggerTypeId, isEdit, eventTypeRsp.data.id);

      const eventTriggerRequest = isEdit
        ? this.updateEventTrigger(deepClean(eventTriggerPayload))
        : this.createEventTrigger(deepClean(eventTriggerPayload))

      const eventTriggerRsp = await lastValueFrom(eventTriggerRequest);

      eventTypeRsp.data.eventTriggers = [eventTriggerRsp.data];

      flow.events = flow.events!.filter(e => e.id > 0);
      flow.events.push({...eventTypeRsp.data, activator: et.activator});
    }

    return flow!;
  }

  setEventTypePayload(et: EventType, isEdit: boolean, flowId: number, originalId: number): {
    eventTriggerTypeId: number,
    eventTypePayload: any
  } {

    // Se configuran el eventClassId y el eventTriggerTypeId del EventType
    let eventClassId = 0;
    let eventTriggerTypeId = 0;
    if (et.activator?.name === 'Manual') {
      eventClassId = 1; // (Manual)
      eventTriggerTypeId = 2 // (Manual)
    } else {
      eventClassId = 2; // (Automatico)
      if (et.activator?.name === 'Fecha Mojito') {
        eventTriggerTypeId = 3 // (Fecha Mojito)
      } else if (et.activator?.name === 'Subida documento') {
        eventTriggerTypeId = 1 // (Subida documento)
      } else {
        eventTriggerTypeId = 4 // (Validacion documento)
      }
    }

    // Luego se crea el payload del POST del EventType
    return {
      eventTriggerTypeId,
      eventTypePayload: {
        name: et.name,
        isActive: true,
        sequence: et.sequence,
        flowId: isEdit ? originalId : flowId,
        eventClassId: eventClassId,
        color: et.color,
        icon: et.icon,
        locationId: et.activator?.location?.id,
        mojitoDynamicField: et.activator?.mojitoDynamicField?.enum,
        id: isEdit ? et.id : null
      }
    }
  }

  async setEventTriggerPayload(et: EventType, eventTriggerTypeId: number, isEdit: boolean, eventTypeId: number): Promise<any> {

    const eventTriggerData = isEdit
      ? await lastValueFrom(this.getEventTrigger({
        startRow: 0,
        endRow: 1,
        sortProperty: 'id',
        sortDirection: 'asc',
        filterProperty: 'eventTypeId',
        filterValue: et.id.toString()
      }))
      : null;

    const eventTriggerId = isEdit
      ? (et.eventTriggers ? et.eventTriggers![0]!.id : eventTriggerData!.data.rows[0].id)
      : null;

    return {
      name: et.activator?.name || 'Manual',
      integrationField: eventTriggerTypeId === 3 ? et.activator?.value : '-',
      eventTypeId: et.id > 0 ? et.id : eventTypeId,
      documentUploaded: eventTriggerTypeId === 1,
      documentValidation: eventTriggerTypeId === 4,
      documentTypeId: (eventTriggerTypeId === 1 || eventTriggerTypeId === 4) ? et.activator!.value : null,
      eventTriggerTypeId,
      id: eventTriggerId,
    }
  }
}
